import { Link } from "react-router-dom";
import { ArrowRight, Award, Heart, MapPin, Sparkles, ShieldCheck, FileCheck2, Calendar, MapPinned } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { LOGO_URL } from "@/lib/api";

const CERTIFICATES = {
  dti: "https://customer-assets.emergentagent.com/job_palawan-wheels/artifacts/3vv7di3f_viber_image_2026-04-27_17-02-26-919.jpg",
  mayors: "https://customer-assets.emergentagent.com/job_palawan-wheels/artifacts/0eb9l1mb_viber_image_2026-03-13_12-22-05-699.jpg",
};

export default function About() {
  const values = [
    { icon: Award, title: "Crafted Quality", desc: "Every vehicle in our fleet is hand-selected for reliability, comfort, and presence on the road." },
    { icon: Heart, title: "Personal Service", desc: "We're a family-run business — every booking is handled with personal care and attention." },
    { icon: MapPin, title: "Local Expertise", desc: "Born and raised in Palawan. We know every road, beach, and hidden corner of this island." },
    { icon: Sparkles, title: "Hassle-Free", desc: "Transparent pricing, fast confirmations, and 24/7 support so you can focus on the journey." },
  ];

  return (
    <div className="min-h-screen">
      <Navbar />
      <section className="hero-bg pt-32 pb-16 noise" data-testid="about-hero">
        <div className="max-w-7xl mx-auto px-6">
          <p className="kicker">About PB-Lux</p>
          <h1 className="font-display text-5xl sm:text-7xl mt-4 leading-[1.05]">
            Born in Palawan. <br />
            <em className="not-italic text-gold-gradient">Made for the road.</em>
          </h1>
        </div>
      </section>

      <section className="section-bg-dark py-20">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-14 items-center">
          <div className="relative">
            <img src={LOGO_URL} alt="PB-Lux" className="w-full max-w-md mx-auto rounded-full" />
          </div>
          <div>
            <p className="kicker">Our Story</p>
            <h2 className="font-display text-4xl mt-4">A Local Dream, Built on Trust.</h2>
            <div className="gold-bar mt-4" />
            <div className="mt-6 space-y-5 text-foreground/70 leading-relaxed">
              <p>
                PB-Lux Vehicle Rental was founded in 2023 in Puerto Princesa, Palawan with a single belief:
                that exploring the world's most beautiful island deserves a vehicle worthy of the journey.
              </p>
              <p>
                What started as a small fleet of two scooters and a sedan has grown into Palawan's most
                trusted premium rental — twenty-three vehicles strong, from city-friendly hatchbacks to
                rugged 4x4 pickups and adventure-ready maxi-scooters.
              </p>
              <p>
                Every guest is family. We hand-deliver vehicles to hotels, walk customers through every
                feature personally, and stand behind our cars 24/7. It's not just about renting wheels —
                it's about helping you write your Palawan story.
              </p>
            </div>
            <Link to="/fleet" className="btn-gold mt-8" data-testid="about-explore-fleet">
              Explore the Fleet <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <section className="section-bg-deep py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto">
            <p className="kicker justify-center">What We Stand For</p>
            <h2 className="font-display text-4xl mt-4">Our Values</h2>
            <div className="gold-bar mt-4" />
          </div>
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((v) => (
              <div key={v.title} className="glass-card rounded-2xl p-7" data-testid={`value-${v.title}`}>
                <div className="w-12 h-12 rounded-full bg-gold-500/10 border border-gold-500/30 flex items-center justify-center">
                  <v.icon className="w-5 h-5 text-gold-400" />
                </div>
                <h3 className="font-display text-2xl mt-5">{v.title}</h3>
                <p className="text-sm text-foreground/65 mt-2 leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Licensed & Verified Business */}
      <section id="credentials-section" className="section-bg-dark py-20" data-testid="credentials-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto">
            <p className="kicker justify-center">
              <ShieldCheck className="w-3 h-3 inline-block" /> Licensed & Verified Business
            </p>
            <h2 className="font-display text-4xl mt-4">Officially Registered. <em className="not-italic text-gold-gradient">Fully Compliant.</em></h2>
            <div className="gold-bar mt-4" />
            <p className="mt-6 text-foreground/65 leading-relaxed">
              PB-Lux Vehicle Rental is a fully registered business in Puerto Princesa, Palawan —
              compliant with the Philippine Department of Trade & Industry (DTI) and the City of Puerto Princesa Mayor's Office.
              You can rent with complete peace of mind.
            </p>
          </div>

          <div className="mt-14 grid lg:grid-cols-2 gap-8">
            {/* DTI */}
            <div className="glass-card rounded-2xl p-6 group" data-testid="dti-certificate">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-12 h-12 rounded-full bg-blue-500/15 border border-blue-500/30 flex items-center justify-center">
                  <FileCheck2 className="w-5 h-5 text-blue-300" />
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-[0.25em] text-blue-300/80">Department of Trade & Industry</p>
                  <h3 className="font-display text-2xl text-gold-200 leading-tight">Certificate of Business Name Registration</h3>
                </div>
              </div>
              <a href={CERTIFICATES.dti} target="_blank" rel="noreferrer" className="block rounded-xl overflow-hidden bg-white" data-testid="dti-link">
                <img src={CERTIFICATES.dti} alt="DTI Certificate" className="w-full h-auto group-hover:scale-[1.02] transition-transform duration-500" />
              </a>
              <div className="mt-5 grid grid-cols-2 gap-3 text-xs">
                <div className="rounded-lg bg-ink-800/60 border border-gold-500/15 p-3">
                  <p className="text-[10px] uppercase tracking-widest text-foreground/50">Business Name No.</p>
                  <p className="mt-1 text-gold-300 font-medium">6060574</p>
                </div>
                <div className="rounded-lg bg-ink-800/60 border border-gold-500/15 p-3">
                  <p className="text-[10px] uppercase tracking-widest text-foreground/50">Owner</p>
                  <p className="mt-1 text-foreground">Pearly Ibañez Bellones</p>
                </div>
                <div className="rounded-lg bg-ink-800/60 border border-gold-500/15 p-3 col-span-2">
                  <p className="text-[10px] uppercase tracking-widest text-foreground/50 flex items-center gap-1.5">
                    <Calendar className="w-3 h-3" /> Validity
                  </p>
                  <p className="mt-1 text-foreground">16 April 2024 — 16 April 2029</p>
                </div>
                <div className="rounded-lg bg-ink-800/60 border border-gold-500/15 p-3 col-span-2">
                  <p className="text-[10px] uppercase tracking-widest text-foreground/50 flex items-center gap-1.5">
                    <MapPinned className="w-3 h-3" /> Jurisdiction
                  </p>
                  <p className="mt-1 text-foreground">City of Puerto Princesa (Capital) · Region IV-B (MIMAROPA)</p>
                </div>
              </div>
            </div>

            {/* Mayor's Permit */}
            <div className="glass-card rounded-2xl p-6 group" data-testid="mayors-permit">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-12 h-12 rounded-full bg-red-500/15 border border-red-500/30 flex items-center justify-center">
                  <ShieldCheck className="w-5 h-5 text-red-300" />
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-[0.25em] text-red-300/80">Office of the City Mayor</p>
                  <h3 className="font-display text-2xl text-gold-200 leading-tight">Mayor's Business Permit 2026</h3>
                </div>
              </div>
              <a href={CERTIFICATES.mayors} target="_blank" rel="noreferrer" className="block rounded-xl overflow-hidden bg-white" data-testid="mayors-link">
                <img src={CERTIFICATES.mayors} alt="Mayor's Permit" className="w-full h-auto group-hover:scale-[1.02] transition-transform duration-500" />
              </a>
              <div className="mt-5 grid grid-cols-2 gap-3 text-xs">
                <div className="rounded-lg bg-ink-800/60 border border-gold-500/15 p-3">
                  <p className="text-[10px] uppercase tracking-widest text-foreground/50">Permit No.</p>
                  <p className="mt-1 text-gold-300 font-medium">BP-2026-08271-0</p>
                </div>
                <div className="rounded-lg bg-ink-800/60 border border-gold-500/15 p-3">
                  <p className="text-[10px] uppercase tracking-widest text-foreground/50">Plate No.</p>
                  <p className="mt-1 text-gold-300 font-medium">30953</p>
                </div>
                <div className="rounded-lg bg-ink-800/60 border border-gold-500/15 p-3 col-span-2">
                  <p className="text-[10px] uppercase tracking-widest text-foreground/50 flex items-center gap-1.5">
                    <Calendar className="w-3 h-3" /> Validity
                  </p>
                  <p className="mt-1 text-foreground">Issued 10 February 2026 — valid until 31 December 2026</p>
                </div>
                <div className="rounded-lg bg-ink-800/60 border border-gold-500/15 p-3 col-span-2">
                  <p className="text-[10px] uppercase tracking-widest text-foreground/50 flex items-center gap-1.5">
                    <MapPinned className="w-3 h-3" /> Business Address
                  </p>
                  <p className="mt-1 text-foreground">Kilos Agad, San Miguel, Puerto Princesa City</p>
                </div>
              </div>
            </div>
          </div>

          <p className="mt-10 text-center text-xs text-foreground/50">
            Click any certificate to view the full-size image · All documents verifiable with DTI Philippines & City of Puerto Princesa
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}
